import { Directive, HostListener, Input } from "@angular/core";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";

@Directive({
	selector: "[copyToClipboard]",
	standalone: false
})
export class CopyToClipboardDirective {
	/**
	 * The value that is copied to the clipboard
	 * pass in falsy values to prevent copying
	 */
	@Input("copyToClipboard") public copyValue: string | false;

	/**
	 *  Optional Info Text displayed in a Toast when value has been copied to clipboard
	 */
	@Input() public copyInfo: string;

	/**
	 *  Optional toast type
	 */
	@Input() public toastType: ToastType = ToastType.info;

	private readonly actions: ToastActions = {
		[ToastType.info]: (infoMessage) => this.infoService.setInfo(infoMessage),
		[ToastType.success]: (infoMessage) => this.infoService.setSuccess(infoMessage)
	};

	public constructor(
		private infoService: InfoService,
		private translationService: TranslationService
	) {}

	@HostListener("keydown.enter")
	@HostListener("click")
	public onClick(): void {
		if (this.copyValue) {
			try {
				this.copyToClipboard(this.copyValue, this.copyInfo);
			} catch (e) {
				console.error(e);
				this.infoService.setError(this.translationService.translate("copy_to_clipboard_not_supported"));
			}
		}
	}

	private copyToClipboard(value: string, infoMessage: string = null): void {
		const input = document.createElement("input");
		input.setAttribute("value", value);
		document.body.appendChild(input);

		if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
			const el = input;
			const editable = el.contentEditable;
			const readOnly = el.readOnly;
			el.contentEditable = "true";
			el.readOnly = false;
			const range = document.createRange();
			range.selectNodeContents(el);
			const sel = window.getSelection();
			sel.removeAllRanges();
			sel.addRange(range);
			el.setSelectionRange(0, 999999);
			el.contentEditable = editable;
			el.readOnly = readOnly;
		} else {
			input.select();
		}

		document.execCommand("copy");
		document.body.removeChild(input);
		if (infoMessage) {
			const action = this.actions[this.toastType ?? ToastType.info];
			action(infoMessage);
		}
	}
}

/** Toast Types */
export enum ToastType {
	info,
	success
}

type ToastActions = {
	[key in ToastType]: (infoMessage: string) => void;
};
